import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rolesService } from "../../services";
import AdminModalTailwind from "./AdminModalTailwind";
import Toast from "./Toast";

const PERMISSION_GROUPS_CONFIG = {
  general: { titleKey: "admin.permissions.group.general", defaultTitle: "Allgemein" },
  participants: { titleKey: "admin.permissions.group.participants", defaultTitle: "Teilnehmer & Nationen" },
  organisation: { titleKey: "admin.permissions.group.organisation", defaultTitle: "Organisation" },
  results: { titleKey: "admin.permissions.group.results", defaultTitle: "Ergebnisse" },
  security: { titleKey: "admin.permissions.group.security", defaultTitle: "Sicherheit" }
};

const PERMISSION_CONFIG = {
  view_dashboard: {
    group: "general",
    labelKey: "admin.permissions.view_dashboard.label",
    defaultLabel: "Dashboard anzeigen",
    descriptionKey: "admin.permissions.view_dashboard.description",
    defaultDescription: "Gewährt Zugriff auf die Dashboard-Übersicht."
  },
  view_results: {
    group: "results",
    labelKey: "admin.permissions.view_results.label",
    defaultLabel: "Ergebnisse einsehen",
    descriptionKey: "admin.permissions.view_results.description",
    defaultDescription: "Erlaubt die Einsicht in veröffentlichte Ergebnisse."
  },
  manage_participants: {
    group: "participants",
    labelKey: "admin.permissions.manage_participants.label",
    defaultLabel: "Teilnehmer verwalten",
    descriptionKey: "admin.permissions.manage_participants.description",
    defaultDescription: "Teilnehmer anlegen, bearbeiten und löschen."
  },
  manage_nations: {
    group: "participants",
    labelKey: "admin.permissions.manage_nations.label",
    defaultLabel: "Nationen verwalten",
    descriptionKey: "admin.permissions.manage_nations.description",
    defaultDescription: "Zugriff auf die Verwaltung der Nationenliste."
  },
  manage_categories: {
    group: "participants",
    labelKey: "admin.permissions.manage_categories.label",
    defaultLabel: "Kategorien verwalten",
    descriptionKey: "admin.permissions.manage_categories.description",
    defaultDescription: "Anlegen und Bearbeiten von Wettkampfkategorien."
  },
  manage_groups: {
    group: "organisation",
    labelKey: "admin.permissions.manage_groups.label",
    defaultLabel: "Gruppen verwalten",
    descriptionKey: "admin.permissions.manage_groups.description",
    defaultDescription: "Zugriff auf Gruppeneinteilungen und Startlisten."
  },
  manage_schedule: {
    group: "organisation",
    labelKey: "admin.permissions.manage_schedule.label",
    defaultLabel: "Zeitplan verwalten",
    descriptionKey: "admin.permissions.manage_schedule.description",
    defaultDescription: "Planung und Anpassung der Wettkampfzeitpläne."
  },
  manage_schedule_settings: {
    group: "organisation",
    labelKey: "admin.permissions.manage_schedule_settings.label",
    defaultLabel: "Zeitplan-Konfiguration verwalten",
    descriptionKey: "admin.permissions.manage_schedule_settings.description",
    defaultDescription: "Zugriff auf Zeitplan-Konfiguration und Einstellungen."
  },
  view_caller: {
    group: "organisation",
    labelKey: "admin.permissions.view_caller.label",
    defaultLabel: "Zugriff auf Calleransicht",
    descriptionKey: "admin.permissions.view_caller.description",
    defaultDescription: "Gewährt Zugriff auf die Caller-Ansicht für Athleten-Aufruf."
  },
  manage_results: {
    group: "results",
    labelKey: "admin.permissions.manage_results.label",
    defaultLabel: "Ergebnisse verwalten",
    descriptionKey: "admin.permissions.manage_results.description",
    defaultDescription: "Bewertungen erfassen, veröffentlichen und anpassen."
  },
  manage_users: {
    group: "security",
    labelKey: "admin.permissions.manage_users.label",
    defaultLabel: "Benutzer verwalten",
    descriptionKey: "admin.permissions.manage_users.description",
    defaultDescription: "Benutzerkonten anlegen, bearbeiten und sperren."
  },
  manage_roles: {
    group: "security",
    labelKey: "admin.permissions.manage_roles.label",
    defaultLabel: "Rollen & Berechtigungen",
    descriptionKey: "admin.permissions.manage_roles.description",
    defaultDescription: "Rollen definieren und deren Berechtigungen anpassen."
  },
  manage_data: {
    group: "security",
    labelKey: "admin.permissions.manage_data.label",
    defaultLabel: "Datenmanagement",
    descriptionKey: "admin.permissions.manage_data.description",
    defaultDescription: "Zugriff auf Datenmanagement und Systemeinstellungen."
  }
};

const PERMISSION_GROUP_ORDER = ["general", "participants", "organisation", "results", "security"];

function UserRolesTailwind() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [updatingRoleId, setUpdatingRoleId] = useState(null);
  const [isRoleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: "", description: "", permissions: [] });
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [isBulkEditMode, setIsBulkEditMode] = useState(false);

  const rolesQuery = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const res = await rolesService.getRoles();
      if (!res.success) throw new Error(res.error || "Fehler beim Laden der Rollen");
      return res.data || [];
    }
  });

  const permissionsQuery = useQuery({
    queryKey: ["permissions"],
    queryFn: async () => {
      const res = await rolesService.getPermissions();
      if (!res.success) throw new Error(res.error || "Fehler beim Laden der Berechtigungen");
      return res.data || [];
    }
  });

  const addToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: "info", message: "" }), 5000);
  };

  const hideToast = () => setToast({ show: false, type: "info", message: "" });

  const createRoleMutation = useMutation({
    mutationFn: rolesService.createRole.bind(rolesService),
    onSuccess: () => {
      queryClient.invalidateQueries(["roles"]);
      addToast("success", t("admin.userRoles.toast.created", "Rolle erfolgreich erstellt"));
      setRoleModalOpen(false);
      setEditingRole(null);
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.userRoles.toast.createError", "Fehler beim Anlegen der Rolle"));
    }
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, payload }) => rolesService.updateRole(id, payload),
    onMutate: (variables) => setUpdatingRoleId(variables.id),
    onSuccess: () => {
      queryClient.invalidateQueries(["roles"]);
      addToast("success", t("admin.userRoles.toast.updated", "Rolle aktualisiert"));
      setRoleModalOpen(false);
      setEditingRole(null);
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.userRoles.toast.updateError", "Fehler beim Aktualisieren der Rolle"));
    },
    onSettled: () => setUpdatingRoleId(null)
  });

  const deleteRoleMutation = useMutation({
    mutationFn: rolesService.deleteRole.bind(rolesService),
    onSuccess: () => {
      queryClient.invalidateQueries(["roles"]);
      addToast("success", t("admin.userRoles.toast.deleted", "Rolle gelöscht"));
      setDeleteModalOpen(false);
      setRoleToDelete(null);
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.userRoles.toast.deleteError", "Fehler beim Löschen der Rolle"));
    }
  });

  const availablePermissions = permissionsQuery.data || [];
  const availablePermissionKeys = new Set(availablePermissions.map((p) => p.key));

  const permissionGroups = useMemo(() => {
    const groupsMap = new Map();

    PERMISSION_GROUP_ORDER.forEach((groupId) => {
      const config = PERMISSION_GROUPS_CONFIG[groupId];
      groupsMap.set(groupId, {
        id: groupId,
        title: t(config?.titleKey || "", config?.defaultTitle || groupId),
        permissions: []
      });
    });

    availablePermissions.forEach((permission) => {
      // Nur Permissions anzeigen, die im PERMISSION_CONFIG definiert sind
      const config = PERMISSION_CONFIG[permission.key];
      if (!config) {
        return; // Überspringe ungenutzte Permissions (manage_settings, view_schedule, view_participants)
      }
      
      const groupId = config.group || "general";
      if (!groupsMap.has(groupId)) {
        groupsMap.set(groupId, {
          id: groupId,
          title: t(PERMISSION_GROUPS_CONFIG[groupId]?.titleKey || "", PERMISSION_GROUPS_CONFIG[groupId]?.defaultTitle || groupId),
          permissions: []
        });
      }
      const group = groupsMap.get(groupId);
      group.permissions.push({
        key: permission.key,
        label: t(config.labelKey || "", config.defaultLabel || permission.key),
        description: t(config.descriptionKey || "", config.defaultDescription || permission.description || "")
      });
    });

    return Array.from(groupsMap.values()).filter((group) => group.permissions.length > 0);
  }, [availablePermissions, t]);

  const roles = rolesQuery.data || [];

  const openCreateModal = () => {
    setEditingRole(null);
    setRoleForm({ name: "", description: "", permissions: [] });
    setRoleModalOpen(true);
  };

  const openEditModal = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name || "",
      description: role.description || "",
      permissions: role.permissions ? role.permissions.map((p) => p.key) : []
    });
    setRoleModalOpen(true);
  };

  const closeRoleModal = () => {
    if (createRoleMutation.isLoading || updateRoleMutation.isLoading) return;
    setRoleModalOpen(false);
    setEditingRole(null);
  };

  const handleRoleFormChange = (event) => {
    const { name, value } = event.target;
    setRoleForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleFormPermissionToggle = (permissionKey) => {
    setRoleForm((prev) => {
      const hasPermission = prev.permissions.includes(permissionKey);
      return {
        ...prev,
        permissions: hasPermission
          ? prev.permissions.filter((key) => key !== permissionKey)
          : [...prev.permissions, permissionKey]
      };
    });
  };

  const handleSubmitRoleForm = () => {
    const payload = {
      name: roleForm.name.trim(),
      description: roleForm.description.trim(),
      permissions: roleForm.permissions.filter((key) => availablePermissionKeys.has(key))
    };

    if (!payload.name) {
      addToast("warning", t("admin.userRoles.validation.nameRequired", "Der Rollenname darf nicht leer sein."));
      return;
    }

    if (editingRole) {
      updateRoleMutation.mutate({ id: editingRole.id, payload });
    } else {
      createRoleMutation.mutate(payload);
    }
  };

  const handleTogglePermission = (role, permissionKey, enable) => {
    const currentKeys = role.permissions ? role.permissions.map((p) => p.key) : [];
    const nextKeys = enable
      ? Array.from(new Set([...currentKeys, permissionKey]))
      : currentKeys.filter((key) => key !== permissionKey);

    updateRoleMutation.mutate({ id: role.id, payload: { permissions: nextKeys } });
  };

  const handleDeleteRole = (role) => {
    setRoleToDelete(role);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteRoleMutation.isLoading) return;
    setDeleteModalOpen(false);
    setRoleToDelete(null);
  };

  const getRoleTag = (roleName) => {
    const key = (roleName || "").toLowerCase();
    const builtIn = ["admin", "caller", "scorekeeper", "superadmin"];
    if (builtIn.includes(key)) {
      return t(`admin.userRoles.roleTag.${key}`);
    }
    return roleName?.trim() || t("admin.userRoles.roleTag.default", "Rolle");
  };

  const getRoleHeaderVisual = (roleName) => {
    const n = (roleName || "").toLowerCase();
    switch (n) {
      case "admin":
        return {
          icon: "fas fa-user-shield",
          wrap: "bg-emerald-100 dark:bg-emerald-900/35 text-emerald-700 dark:text-emerald-300 ring-emerald-200/60 dark:ring-emerald-700/40"
        };
      case "scorekeeper":
        return {
          icon: "fas fa-clipboard-list",
          wrap: "bg-blue-100 dark:bg-blue-900/35 text-blue-700 dark:text-blue-300 ring-blue-200/60 dark:ring-blue-700/40"
        };
      case "caller":
        return {
          icon: "fas fa-bullhorn",
          wrap: "bg-violet-100 dark:bg-violet-900/35 text-violet-700 dark:text-violet-300 ring-violet-200/60 dark:ring-violet-700/40"
        };
      case "superadmin":
        return {
          icon: "fas fa-crown",
          wrap: "bg-amber-100 dark:bg-amber-900/35 text-amber-800 dark:text-amber-300 ring-amber-200/60 dark:ring-amber-700/40"
        };
      default:
        return {
          icon: "fas fa-user-tag",
          wrap: "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 ring-slate-200 dark:ring-slate-600"
        };
    }
  };

  // Get all permissions in order
  const allPermissions = useMemo(() => {
    const perms = [];
    PERMISSION_GROUP_ORDER.forEach((groupId) => {
      const group = permissionGroups.find(g => g.id === groupId);
      if (group) {
        group.permissions.forEach(permission => {
          perms.push({ ...permission, groupId, groupTitle: group.title });
        });
      }
    });
    return perms;
  }, [permissionGroups]);

  if (rolesQuery.isLoading || permissionsQuery.isLoading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64 mb-2"></div>
            <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
          </div>

          {/* Controls Skeleton - Right aligned */}
          <div className="mb-6 flex items-center justify-end gap-2 animate-pulse">
            <div className="w-36 h-9 bg-slate-200 dark:bg-slate-700 rounded-xl" />
            <div className="w-40 h-9 bg-slate-200 dark:bg-slate-700 rounded-xl" />
          </div>

          {/* Table Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-6 py-3">
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32" />
                    </th>
                    {[...Array(4)].map((_, i) => (
                      <th key={i} className="px-6 py-3">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20 mx-auto" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {[...Array(15)].map((_, i) => (
                    <tr key={i}>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-48" />
                      </td>
                      {[...Array(4)].map((_, j) => (
                        <td key={j} className="px-6 py-4 text-center">
                          <div className="h-8 w-8 bg-slate-200 dark:bg-slate-700 rounded-full mx-auto"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 dark:bg-slate-700/30">
                  <tr>
                    <td colSpan={5} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-48"></div>
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-40"></div>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (rolesQuery.isError || permissionsQuery.isError) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-2xl mb-4">
                <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400" aria-hidden />
              </div>
              <p className="text-slate-600 dark:text-slate-400 font-medium mb-4">
                {rolesQuery.error?.message || permissionsQuery.error?.message || t("admin.userRoles.error.load", "Fehler beim Laden der Rollen.")}
              </p>
              <button
                type="button"
                onClick={() => {
                  rolesQuery.refetch();
                  permissionsQuery.refetch();
                }}
                className="inline-flex items-center gap-2 h-9 px-4 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-xl hover:bg-primary-dark dark:hover:bg-primary transition-colors shadow-sm"
              >
                <i className="fas fa-redo text-xs" aria-hidden />
                {t("admin.retry", "Erneut versuchen")}
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.userRoles.title", "Nutzerrollen & Berechtigungen")}
                </h1>
                {roles.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                    <i className="fas fa-shield-alt text-[10px]" aria-hidden />
                    {t("admin.userRoles.badge", "{{count}} Rollen", { count: roles.length })}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5 max-w-3xl leading-relaxed">
                {t("admin.userRoles.subtitleReal", "Verwalte Rollen, definiere Berechtigungen und steuere den Zugriff auf Verwaltungsbereiche im direkten Vergleich.")}
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6 flex items-center justify-end gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setIsBulkEditMode(!isBulkEditMode)}
            className={`inline-flex items-center gap-2 px-3.5 py-2 h-9 text-sm font-medium rounded-xl border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 ${
              isBulkEditMode
                ? "bg-primary dark:bg-primary-light text-white border-primary/30 dark:border-primary-light/30 hover:bg-primary-dark dark:hover:bg-primary shadow-sm"
                : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
            }`}
          >
            <i className={`fas ${isBulkEditMode ? "fa-check" : "fa-th"} text-xs`} aria-hidden />
            {isBulkEditMode
              ? t("admin.userRoles.actions.bulkEditDone", "Bearbeiten beenden")
              : t("admin.userRoles.actions.bulkEdit", "Matrix bearbeiten")}
          </button>
          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 h-9 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-xl hover:bg-primary-dark dark:hover:bg-primary transition-colors shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
          >
            <i className="fas fa-plus text-xs" aria-hidden />
            {t("admin.userRoles.actions.addRole", "Rolle hinzufügen")}
          </button>
        </div>

        {roles.length > 0 && (
          <div
            className={`mb-4 flex gap-3 rounded-xl border px-4 py-3 sm:py-3.5 ${
              isBulkEditMode
                ? "border-primary/25 bg-primary/5 dark:bg-primary-light/10 dark:border-primary-light/25"
                : "border-slate-200 bg-slate-50 dark:border-slate-600 dark:bg-slate-800/60"
            }`}
            role="status"
          >
            <div
              className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                isBulkEditMode
                  ? "bg-primary/15 text-primary dark:bg-primary-light/20 dark:text-primary-light"
                  : "bg-white text-slate-500 shadow-sm ring-1 ring-slate-200 dark:bg-slate-700 dark:text-slate-400 dark:ring-slate-600"
              }`}
              aria-hidden
            >
              <i className={`fas ${isBulkEditMode ? "fa-hand-pointer" : "fa-lock"} text-sm`} />
            </div>
            <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200 min-w-0">
              {isBulkEditMode
                ? t("admin.userRoles.editModeHintOn", "Bearbeitungsmodus aktiv: Klicke auf die Kreise in der Matrix, um Berechtigungen zu setzen oder zu entfernen.")
                : t("admin.userRoles.editModeHintOff", "Aktiviere oben „Matrix bearbeiten“, um Berechtigungen in der Tabelle per Klick zu ändern.")}
            </p>
          </div>
        )}

        {/* Permissions Table */}
        {roles.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider min-w-[12rem]">
                      {t("admin.userRoles.table.permission", "Berechtigung")}
                    </th>
                    {roles.map((role) => {
                      const visual = getRoleHeaderVisual(role.name);
                      return (
                        <th key={role.id} className="px-6 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 min-w-[9.5rem]">
                          <div className="flex flex-col items-center gap-2">
                            <div className="flex items-center justify-center gap-2">
                              <div
                                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1 ${visual.wrap}`}
                                aria-hidden
                              >
                                <i className={`${visual.icon} text-sm`} />
                              </div>
                              <span className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate max-w-[7rem]">
                                {role.name}
                              </span>
                            </div>
                            <div className="flex items-center justify-center gap-1">
                              <button
                                type="button"
                                onClick={() => openEditModal(role)}
                                className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary-light/30 transition-colors"
                                title={t("admin.userRoles.actions.editRole", "Rolle bearbeiten")}
                              >
                                <i className="fas fa-edit text-[11px]" aria-hidden />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteRole(role)}
                                className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-700/50 transition-colors"
                                title={t("admin.userRoles.actions.deleteRole", "Rolle löschen")}
                              >
                                <i className="fas fa-trash text-[11px]" aria-hidden />
                              </button>
                            </div>
                            <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400 leading-tight px-1">
                              {t("admin.userRoles.table.roleMeta", "ID {{id}} · {{tag}}", {
                                id: role.id,
                                tag: getRoleTag(role.name)
                              })}
                            </span>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-800">
                  {PERMISSION_GROUP_ORDER.map((groupId) => {
                    const group = permissionGroups.find(g => g.id === groupId);
                    if (!group || group.permissions.length === 0) return null;
                    
                    return (
                      <React.Fragment key={groupId}>
                        {/* Group Header Row */}
                        <tr className="bg-slate-50 dark:bg-slate-800/80 border-y border-slate-200 dark:border-slate-700">
                          <td colSpan={roles.length + 1} className="px-6 py-2.5">
                            <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                              {group.title}
                            </h3>
                          </td>
                        </tr>
                        {/* Permission Rows */}
                        {group.permissions.map((permission) => {
                          return (
                            <tr key={permission.key} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  {permission.description ? (
                                    <span
                                      className="inline-flex size-5 shrink-0 items-center justify-center text-slate-400 dark:text-slate-500"
                                      title={permission.description}
                                    >
                                      <i className="fas fa-info-circle text-sm leading-none cursor-help" aria-hidden />
                                    </span>
                                  ) : null}
                                  <span
                                    className="min-w-0 text-sm font-medium leading-snug text-slate-900 dark:text-slate-100"
                                    title={permission.description || undefined}
                                  >
                                    {permission.label}
                                  </span>
                                </div>
                              </td>
                              {roles.map((role) => {
                                const isUpdating = updatingRoleId === role.id;
                                const rolePermissions = new Set(role.permissions ? role.permissions.map((p) => p.key) : []);
                                const hasPermission = rolePermissions.has(permission.key);
                                
                                return (
                                  <td key={`${role.id}-${permission.key}`} className="px-6 py-4 text-center">
                                    {isUpdating && updatingRoleId === role.id ? (
                                      <div className="inline-flex items-center justify-center w-8 h-8">
                                        <i className="fas fa-spinner fa-spin text-primary dark:text-primary-light text-xs" aria-hidden />
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => isBulkEditMode && handleTogglePermission(role, permission.key, !hasPermission)}
                                        disabled={!isBulkEditMode}
                                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
                                          hasPermission
                                            ? "bg-primary dark:bg-primary-light border-primary dark:border-primary-light text-white shadow-sm"
                                            : "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-transparent"
                                        } ${
                                          isBulkEditMode
                                            ? "hover:border-primary dark:hover:border-primary-light cursor-pointer enabled:hover:shadow-sm"
                                            : "cursor-not-allowed opacity-55"
                                        }`}
                                        title={
                                          !isBulkEditMode
                                            ? t("admin.userRoles.enableEditMode", "Aktiviere den Bearbeitungsmodus, um Berechtigungen zu ändern")
                                            : hasPermission
                                              ? t("admin.userRoles.removePermission", "Berechtigung entfernen")
                                              : t("admin.userRoles.addPermission", "Berechtigung hinzufügen")
                                        }
                                        aria-pressed={hasPermission}
                                        aria-label={
                                          hasPermission
                                            ? t("admin.userRoles.removePermission", "Berechtigung entfernen")
                                            : t("admin.userRoles.addPermission", "Berechtigung hinzufügen")
                                        }
                                      >
                                        {hasPermission ? <i className="fas fa-check text-[11px]" aria-hidden /> : null}
                                      </button>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                {/* Table Footer */}
                <tfoot className="bg-slate-50 dark:bg-slate-700/30 border-t border-slate-200 dark:border-slate-700">
                  <tr>
                    <td colSpan={roles.length + 1} className="px-6 py-4">
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600 dark:text-slate-400">
                        <span className="font-medium text-slate-800 dark:text-slate-200 tabular-nums">
                          {t("admin.userRoles.footer.roles", "{{count}} Rollen", { count: roles.length })}
                        </span>
                        <span className="font-medium text-slate-800 dark:text-slate-200 tabular-nums">
                          {t("admin.userRoles.footer.permissions", "{{count}} Berechtigungstypen", {
                            count: allPermissions.length
                          })}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {roles.length === 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm text-center py-16 px-4">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-4 ring-1 ring-slate-200/80 dark:ring-slate-600/80">
              <i className="fas fa-shield-alt text-slate-400 dark:text-slate-500 text-2xl" aria-hidden />
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
              {t("admin.userRoles.empty.title", "Noch keine Rollen vorhanden")}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto leading-relaxed">
              {t("admin.userRoles.empty.subtitle", "Lege deine erste Rolle an, um Zugriffe zu strukturieren.")}
            </p>
            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-4 py-2.5 h-10 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-xl hover:bg-primary-dark dark:hover:bg-primary transition-colors shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            >
              <i className="fas fa-plus text-xs" aria-hidden />
              {t("admin.userRoles.actions.addFirstRole", "Erste Rolle anlegen")}
            </button>
          </div>
        )}
      </div>

      <AdminModalTailwind
        isOpen={isRoleModalOpen}
        onClose={closeRoleModal}
        title={
          editingRole
            ? t("admin.userRoles.modal.editTitle", "Rolle bearbeiten")
            : t("admin.userRoles.modal.createTitle", "Neue Rolle anlegen")
        }
        subtitle={
          editingRole
            ? t("admin.userRoles.modal.editSubtitle", "Passe Namen, Beschreibung und Berechtigungen dieser Rolle an.")
            : t("admin.userRoles.modal.createSubtitle", "Definiere eine neue Rolle inklusive Berechtigungen.")
        }
        onSave={handleSubmitRoleForm}
        onCancel={closeRoleModal}
        saveButtonText={
          editingRole
            ? t("admin.userRoles.modal.save", "Änderungen speichern")
            : t("admin.userRoles.modal.create", "Rolle erstellen")
        }
        cancelButtonText={t("admin.cancel", "Abbrechen")}
        isSaving={createRoleMutation.isLoading || updateRoleMutation.isLoading}
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="role-name" className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                {t("admin.userRoles.form.name", "Rollenname")} <span className="text-red-500 normal-case">*</span>
              </label>
              <input
                id="role-name"
                name="name"
                type="text"
                value={roleForm.name}
                onChange={handleRoleFormChange}
                placeholder={t("admin.userRoles.form.namePlaceholder", "z.B. Scorekeeper")}
                className="w-full h-10 px-3 border border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="role-description" className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                {t("admin.userRoles.form.description", "Beschreibung")}
              </label>
              <input
                id="role-description"
                name="description"
                type="text"
                value={roleForm.description}
                onChange={handleRoleFormChange}
                placeholder={t("admin.userRoles.form.descriptionPlaceholder", "Kurze Beschreibung der Rolle")}
                className="w-full h-10 px-3 border border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
              {t("admin.userRoles.form.permissions", "Berechtigungen")} <span className="text-red-500 normal-case">*</span>
            </label>
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">
                {t("admin.userRoles.form.permissionsHint", "Wähle aus, welche Bereiche diese Rolle sehen oder verwalten darf.")}
              </p>
              <div className="space-y-5 max-h-96 overflow-y-auto pr-2">
                {permissionGroups.map((group) => (
                  <div key={`modal-${group.id}`} className="space-y-2">
                    <h4 className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 px-1 pb-2 border-b border-slate-200 dark:border-slate-600">
                      {group.title}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {group.permissions.map((permission) => {
                        const checked = roleForm.permissions.includes(permission.key);
                        return (
                          <label
                            key={`modal-${permission.key}`}
                            className={`flex items-center justify-between gap-3 p-3 rounded-xl border text-sm transition-colors cursor-pointer ${
                              checked
                                ? "bg-primary/8 dark:bg-primary-light/10 border-primary/40 dark:border-primary-light/40 ring-1 ring-primary/15"
                                : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500"
                            }`}
                          >
                            <span className="flex-1 min-w-0">
                              <span className="font-medium text-slate-900 dark:text-slate-100 block">
                                {permission.label}
                              </span>
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                handleRoleFormPermissionToggle(permission.key);
                              }}
                              className={`inline-flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all flex-shrink-0 ${
                                checked
                                  ? 'bg-primary dark:bg-primary-light border-primary dark:border-primary-light text-white'
                                  : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-transparent hover:border-primary dark:hover:border-primary-light'
                              }`}
                            >
                              {checked && <i className="fas fa-check text-xs"></i>}
                            </button>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </AdminModalTailwind>

      <AdminModalTailwind
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        title={t("admin.userRoles.delete.title", "Rolle löschen")}
        subtitle={
          roleToDelete
            ? t("admin.userRoles.delete.subtitle", 'Soll die Rolle "{{name}}" wirklich gelöscht werden?', {
                name: roleToDelete.name
              })
            : ""
        }
        onSave={() => {
          if (!roleToDelete || deleteRoleMutation.isLoading) return;
          deleteRoleMutation.mutate(roleToDelete.id);
        }}
        onCancel={closeDeleteModal}
        saveButtonText={t("admin.userRoles.delete.confirm", "Löschen")}
        cancelButtonText={t("admin.cancel", "Abbrechen")}
        isSaving={deleteRoleMutation.isLoading}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 rounded-xl">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 rounded-xl flex items-center justify-center shrink-0">
              <i className="fas fa-exclamation-triangle text-sm" aria-hidden />
            </div>
            <p className="text-sm text-red-700 dark:text-red-300">
              {t("admin.userRoles.delete.warning", "Diese Aktion kann nicht rückgängig gemacht werden. Alle Berechtigungszuweisungen gehen verloren.")}
            </p>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t("admin.userRoles.delete.message", "Stelle sicher, dass keine Benutzer mehr diese Rolle benötigen.")}
          </p>
        </div>
      </AdminModalTailwind>

      <Toast message={toast.message} type={toast.type} isVisible={toast.show} onClose={hideToast} />
    </main>
  );
}

export default React.memo(UserRolesTailwind);

